/**
 * MED-1147 — Internal-awareness indicator: a small info icon shown next to an
 * internal session label when, and only when, a CM has set a participant-facing
 * `display_name` override (MED-832) that DIFFERS from the canonical resolved
 * topic the internal user is looking at.
 *
 * The split is deliberate (MED-1106 AC-L4 / MED-832): coaches/CMs see the
 * canonical topic, participants see the override. This component is the
 * at-a-glance signal that the two diverge — its tooltip carries the value
 * ("Participants see: {display_name}"). It is purely additive (AC7) and
 * internal-only (AC8); it renders NOTHING in the common case (no override, or
 * override equal to canonical).
 *
 * Two entry points, ONE divergence rule:
 *   - Coach surfaces pass the server-computed `differs` boolean
 *     (`participant_display_differs`, the single source of truth from
 *     sessionTopicSql.ts) — no client recompute.
 *   - The CM surface has the override + canonical client-side but no server
 *     boolean, so it passes `canonicalTopic` and this component applies
 *     {@link participantLabelDiffers}, which mirrors the server rule exactly
 *     (non-empty after trim AND case-insensitively unequal).
 *
 * Accessibility (AC2): the tooltip is keyboard-focusable (the icon sits in a
 * `tabIndex={0}` span with an `aria-label`) and reveals on hover, focus, AND
 * touch — never hover-only — matching the app's existing accessible-tooltip
 * convention (`events={{ hover, focus, touch }}`).
 */

import { Tooltip } from '@mantine/core';
import { Info } from 'react-feather';

/**
 * The MED-1147 divergence rule, client-side mirror of
 * `PARTICIPANT_DISPLAY_DIFFERS_EXPR` (sessionTopicSql.ts): true only when the
 * participant override is set (non-empty after trim) AND differs
 * case-insensitively from the canonical resolved topic. Used by surfaces that
 * have both values but no server-computed boolean (the CM session list).
 */
export function participantLabelDiffers(
  canonicalTopic: string | null | undefined,
  participantDisplayName: string | null | undefined,
): boolean {
  const name = participantDisplayName?.trim();
  if (!name) return false;
  const canonical = canonicalTopic?.trim() ?? '';
  return name.toLowerCase() !== canonical.toLowerCase();
}

export interface ParticipantLabelDivergenceIndicatorProps {
  /** The participant-facing `display_name` override; shown in the tooltip. */
  participantDisplayName?: string | null;
  /**
   * Server-computed divergence boolean (coach surfaces,
   * `participant_display_differs`). When provided it is authoritative and no
   * client comparison runs.
   */
  differs?: boolean;
  /**
   * Canonical resolved topic (CM surface) — used to compute divergence via
   * {@link participantLabelDiffers} when `differs` is not provided.
   */
  canonicalTopic?: string | null;
  /** Icon size in px. Defaults to 14 to sit comfortably beside body text. */
  size?: number;
}

export function ParticipantLabelDivergenceIndicator({
  participantDisplayName,
  differs,
  canonicalTopic,
  size = 14,
}: ParticipantLabelDivergenceIndicatorProps) {
  const name = participantDisplayName?.trim();
  // Nothing to point at without an override value (the tooltip needs it). This
  // also covers the common "no override" case server-side returns.
  if (!name) return null;

  const isDiverging = differs ?? participantLabelDiffers(canonicalTopic, name);
  if (!isDiverging) return null;

  const label = `Participants see: ${name}`;

  return (
    <Tooltip
      label={label}
      multiline
      withArrow
      withinPortal
      maw={260}
      events={{ hover: true, focus: true, touch: true }}
    >
      <span
        tabIndex={0}
        role="img"
        aria-label={label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          verticalAlign: 'middle',
          flexShrink: 0,
          cursor: 'help',
          color: 'var(--mantine-color-dimmed)',
        }}
      >
        <Info size={size} aria-hidden="true" />
      </span>
    </Tooltip>
  );
}
